import { String } from 'sugar';
import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { consume } from '@lit/context';
import { ContextChangeController } from '../../../ns-services/context-change.js';
import { nsStripeServiceContext, nsDirectoryStateContext } from '../../../ns-services/contexts.js';

import type { NSInputFile } from '../../../ns-elements/ns-input-field.js';

import { nsLayoutStyles } from '../../../ns-styles/index.js';

import { NSStripeService } from '../../../ns-services/ns-stripe-service.js';
import { FinalFormController, FormConfig } from '../../../ns-form/final-form-controller.js';

import { callLambda } from '../../../helpers/lambda.js';
import { renderFormErrorSpan } from '../../../helpers/dom.js';

import Person from '../../../models/person.js';
import JobSchema from '../../../models/job-schema.js';

import NSJobComponent from '../job.js';
import Subscription from '../../../models/subscription.js';

import { LiveAnnouncer } from '../../../helpers/live-announcer.js';
import '../../../ns-elements/ns-job-layout.js';
import '../../../ns-elements/ns-summary-card.js';
import { renderMoney } from '../../../helpers/format-render.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/onboarding-payment.json';
import { jobSchema } from '../job-schema.js';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  address1: string;
  address2: string;
  city: string;
  postcode: string;
  country: string;
}

interface CustomerDetails {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  mobileNumber?: string;
  address: {
    address1?: string;
    address2?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
}
@customElement(NSOnboardingPayment.is)
export default class NSOnboardingPayment extends NSJobComponent {
  static get is() {
    return 'ns-onboarding-payment';
  }

  private __profileChanges = new ContextChangeController(this, nsDirectoryStateContext, () => {
    if (!this._directory || !this._directory.me) throw new Error('Can not find directory service profile');
    this.__profile = this._directory.me;
  });

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  private __createFormController: FinalFormController<FormValues>;

  private __customerDetails?: CustomerDetails;

  @state()
  private __profile?: Person;

  @state()
  private __clientSecret?: string;

  @state()
  private __subscription?: Subscription;

  @state()
  private __succeeded?: boolean;

  private __announcer = new LiveAnnouncer(this);

  @state()
  private __customerConfirmed: boolean = false;

  @state()
  private __selectedPlan?: 'MONTHLY' | 'YEARLY';

  @state()
  private __pricing?: { monthly: number; yearly: number } | null;

  private __errors: Partial<Record<keyof FormValues, string>> = {};

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .plan.selected {
        background: var(--ns-theme-green);
      }

      .plan .desc {
        font-size: var(--wa-font-size-xs);
        font-weight: 300;
      }

      .plan .fee {
        font-size: var(--wa-font-size-l);
      }

      .plan .fee span {
        font-size: var(--wa-font-size-xs);
      }

      .field + .field,
      .field + .fieldset {
        margin-top: var(--wa-space-m);
      }

      .brand-icon {
        width: 3.5rem;
        height: 2rem;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
    `,
  ];

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  constructor() {
    super();

    this.__createFormController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__createCustomerFormSubmit(values),
      validate: () => {
        const errors: Partial<Record<keyof FormValues, string>> = {};
        return errors;
      },
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Auth service is required!');

    if (!this._directory) throw new Error('Unable to find NSDirectoryService');

    if (!this.__stripe) throw new Error('Unable to find stripe service');

    if (!this._job) throw new Error('No job available');

    const subscriptionMetadata = this._job.metadata.find((m) => m.key === 'SUBSCRIPTION_ID');
    if (subscriptionMetadata && subscriptionMetadata.value) {
      this.__subscription = await this._db.getById('subscription', subscriptionMetadata.value);
    }

    const paymentMetadata = this._job.metadata.find((m) => m.key === 'PAYMENT');
    if (paymentMetadata && paymentMetadata.value === 'PAID') {
      this.__succeeded = true;
      this.__announcer.announce('Payment successful.');
    }
    if (paymentMetadata && paymentMetadata.value !== 'PAID') {
      this.__succeeded = false;
      this.__announcer.announce('Payment was not completed.');
    }

    if (this.__subscription) {
      this._subscriptions.subscribe(`subscription.${this.__subscription?.id}.*`, () => {
        this.__subscription = this._db?.get(`subscription.${this.__subscription?.id}`);
      });

      this.__selectPaymentPlan(this.__subscription?.plan);
      this.__customerDetails = {
        firstName: this.__subscription.billing.customer.name.split(' ').shift(),
        lastName: this.__subscription.billing.customer.name.split(' ').pop(),
        emailAddress: this.__subscription.billing.customer.email,
        mobileNumber: this.__subscription.billing.customer.mobileNumber,
        address: {
          address1: this.__subscription.billing.customer.address.address1,
          address2: this.__subscription.billing.customer.address.address2,
          postcode: this.__subscription.billing.customer.address.postcode,
          city: this.__subscription.billing.customer.address.city,
          country: this.__subscription.billing.customer.address.country,
        },
      };
    }

    await this._directory.initialised;
    this.__profile = this._directory.me;

    if (!this._directory.company) throw new Error('Could not find company linked to this user profile');

    this.loading('Fetching tier pricing');
    try {
      const pricingResult = await callLambda(
        'payment/pricing',
        'GET',
        this._auth.token,
        undefined,
        `jobId=${this._job.id}`,
      );
      const { pricing } = pricingResult.res as unknown as {
        tier: string | null;
        pricing: { monthly: number; yearly: number } | null;
      };
      this.__pricing = pricing;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to fetch subscription pricing';
      this.triggerCardError(message);
    } finally {
      this.loaded();
    }

    // this.__updateCardView(true);
    await this.requestUpdate();
  }

  async disconnectedCallback() {
    this.__stripe?.cleanElements();

    super.disconnectedCallback();
  }

  private async __createCustomerFormSubmit(values: Partial<FormValues>) {
    this.__errors = {};

    if (!this.__profile) throw new Error('Unable to find user profile');
    if (!values.firstName && !this.__profile.forename) this.__errors.firstName = 'Missing required customer first name';
    if (!values.lastName && !this.__profile.surname) this.__errors.lastName = 'Missing required customer last name';
    if (!values.email && !this.__profile.email) this.__errors.email = 'Missing required customer email';
    if (!values.mobileNumber) this.__errors.mobileNumber = 'Missing required customer mobile number';
    if (!values.address1) this.__errors.address1 = 'Missing required customer address1';
    if (!values.postcode) this.__errors.postcode = 'Missing required customer postcode';
    if (!values.city) this.__errors.city = 'Missing required customer city';
    if (!values.country) this.__errors.country = 'Missing required customer country';

    if (Object.keys(this.__errors).length > 0) throw new Error('There are some missing required values');

    await this.__submitCustomerDetails(values);
  }

  private async __submitCustomerDetails(values: Partial<FormValues> = {}) {
    if (!this._job || !this._job.id) throw new Error('Missing job info in the job card');
    this.clearError();

    try {
      if (!this._directory) throw new Error('Unable to find NSDirectoryService');
      if (!this._db) throw new Error('Unable to find db service');
      if (!this._auth) throw new Error('Can not find auth service');
      if (!this.__stripe) throw new Error('Unable to find stripe service');
      if (!this.__profile) throw new Error('Could not fetch profile from the NSDirectoryService');
      if (!this.__profile.id) throw new Error('Could not find profile ID');

      if (!this._directory.company) throw new Error('Could not find company linked to this user profile');

      if (!this.__selectedPlan) throw new Error('Can not complete payment without choosing a plan');

      const firstName = this.__subscription?.billing?.customer?.name?.split(' ').shift();
      const lastName = this.__subscription?.billing?.customer?.name?.split(' ').pop();
      const mobileNumber = this.__subscription?.billing?.customer?.mobileNumber;
      const address1 = this.__subscription?.billing?.customer?.address?.address1;
      const address2 = this.__subscription?.billing?.customer?.address?.address2;
      const postcode = this.__subscription?.billing?.customer?.address?.postcode;
      const city = this.__subscription?.billing?.customer?.address?.city;
      const country = this.__subscription?.billing?.customer?.address?.country;

      const customerDetails = {
        firstName: values.firstName
          ? values.firstName
          : this.__profile.forename
            ? this.__profile.forename
            : firstName
              ? firstName
              : '',
        lastName: values.lastName
          ? values.lastName
          : this.__profile.surname
            ? this.__profile.surname
            : lastName
              ? lastName
              : '',
        emailAddress: values.email ? values.email : this.__profile.email ? this.__profile.email : '',
        mobileNumber: values.mobileNumber ? values.mobileNumber : mobileNumber ? mobileNumber : '',
        address: {
          address1: values.address1 ? values.address1 : address1 ? address1 : '',
          address2: values.address2 ? values.address2 : address2 ? address2 : '',
          postcode: values.postcode ? values.postcode : postcode ? postcode : '',
          city: values.city ? values.city : city ? city : '',
          country: values.country ? values.country : country ? country : '',
        },
      };

      this.loading('Creating a payment intent');
      const validation = this.__validate(customerDetails);
      if (validation) {
        this.loaded();
        return;
      }

      const output = await callLambda(
        'payment/subscribe',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          customerInfo: customerDetails,
          plan: this.__selectedPlan,
          provider: 'STRIPE',
        }),
      );

      this.__customerDetails = customerDetails;

      const res = output.res as unknown as { clientSecret: string };
      if (!res.clientSecret) throw new Error('Did not receive a client secret back from the lambda');
      this.__clientSecret = res.clientSecret;
      await this.__stripe.mountStripeElement(this, res.clientSecret);
      this.loaded();

      this.__customerConfirmed = true;
    } catch (err) {
      this.loaded();
      this.showError(err);
    }
  }

  private __validate(customerDetails: CustomerDetails): boolean {
    let err = false;
    if (!customerDetails) throw new Error('Can not find billing details');

    Object.keys(customerDetails).forEach((key: string) => {
      const input: NSInputFile | null = this.renderRoot.querySelector(`.${String.dasherize(key)}`);
      if (input && !input.value && input.required) {
        input.setCustomValidity(`${String.capitalize(String.spacify(key), true, true)} is required`);
        input.reportValidity();
        // this.__updateCardView();
        err = true;
      }
    });

    return err;
  }

  private async __completePayment() {
    if (!this.__stripe) throw new Error('Unable to find stripe service');
    if (!this._job) throw new Error('Unable to find job');

    this.loading('Processing Payment');
    await this.__stripe.proceedPayment(this._job.id);
    this.loaded();
  }

  // The plan blocks render selected from __selectedPlan, so this works before they exist, as when
  // a saved subscription's plan is restored on load ahead of the pricing
  private __selectPaymentPlan(plan: string | undefined) {
    if (!this._job || !this._job.id) throw new Error('Missing job info in the job card');
    this.clearError();

    if (plan !== 'MONTHLY' && plan !== 'YEARLY') return;
    this.__selectedPlan = plan;
  }

  private __renderSubscriptionPlan() {
    return html`
      <div class="job-panel">
        <h3 id="subscription-plan-heading">Choose Your Subscription</h3>
        ${!this.__pricing
          ? html`
              <p>Pricing isn't available yet.</p>
            `
          : html`
              <div role="group" aria-labelledby="subscription-plan-heading">
                <div id="monthly-plan" class="job-option plan ${this.__selectedPlan === 'MONTHLY' ? 'selected' : ''}">
                  <div class="layout vertical flex">
                    <strong>Monthly</strong>
                    <span class="desc">Month-to-month flexibility</span>
                  </div>
                  <strong class="fee">
                    ${renderMoney(this.__pricing.monthly, 'GBP')}
                    <span>/m</span>
                  </strong>
                  ${this.__renderPlanButton('MONTHLY')}
                </div>
                <div id="yearly-plan" class="job-option plan ${this.__selectedPlan === 'YEARLY' ? 'selected' : ''}">
                  <div class="layout vertical flex">
                    <strong>Yearly</strong>
                    <span class="desc">Pay ${renderMoney(this.__pricing.yearly, 'GBP')} upfront and save 20%</span>
                  </div>
                  <strong class="fee">
                    ${renderMoney(this.__pricing.yearly / 12, 'GBP')}
                    <span>/m</span>
                  </strong>
                  ${this.__renderPlanButton('YEARLY')}
                </div>
              </div>
            `}
      </div>
    `;
  }

  private __renderCustomerForm() {
    const { form, register } = this.__createFormController;

    return html`
      ${when(
        this.__customerDetails,
        () => html`
          <div id="created-customer" class="job-panel">
            <h3>Customer Details</h3>
            <ns-summary-card
              heading="${this.__customerDetails?.firstName} ${this.__customerDetails?.lastName}"
              .rows=${[
                { label: 'Email', value: this.__customerDetails?.emailAddress },
                { label: 'Phone Number', value: this.__customerDetails?.mobileNumber },
                { label: 'Address1', value: this.__customerDetails?.address.address1 },
                { label: 'Address2', value: this.__customerDetails?.address.address2 },
                { label: 'Postcode', value: this.__customerDetails?.address.postcode },
                { label: 'City', value: this.__customerDetails?.address.city },
                { label: 'Country', value: this.__customerDetails?.address.country },
              ]}
              ?confirmed=${this.__customerConfirmed || this.__succeeded === true}
              @confirm=${() => this.__submitCustomerDetails()}
            ></ns-summary-card>
          </div>
        `,
        () => html`
          <div class="job-panel">
            <h3>Customer Details</h3>
            <form
              @submit=${(e: CustomEvent) => {
                e.preventDefault();
                form.submit();
              }}
            >
              <div class="field layout vertical">
                <ns-input-field
                  stacked
                  required
                  ${register('firstName')}
                  placeholder="First Name"
                  label="First Name*"
                  value="${this.__profile?.forename}"
                  autocomplete="given-name"
                ></ns-input-field>
                ${renderFormErrorSpan('firstName', this.__errors)}
              </div>
              <div class="field layout vertical">
                <ns-input-field
                  stacked
                  required
                  ${register('lastName')}
                  placeholder="Last Name"
                  label="Last Name*"
                  value="${this.__profile?.surname}"
                  autocomplete="family-name"
                ></ns-input-field>
                ${renderFormErrorSpan('lastName', this.__errors)}
              </div>
              <div class="field layout vertical">
                <ns-input-field
                  stacked
                  required
                  ${register('email')}
                  placeholder="Email"
                  label="Email*"
                  value="${this.__profile?.email}"
                  autocomplete="email"
                ></ns-input-field>
                ${renderFormErrorSpan('email', this.__errors)}
              </div>
              <div class="field layout vertical">
                <ns-input-field
                  stacked
                  required
                  ${register('mobileNumber')}
                  type="tel"
                  placeholder="Mobile Number"
                  label="Mobile Number*"
                  autocomplete="mobile tel"
                ></ns-input-field>
                ${renderFormErrorSpan('mobileNumber', this.__errors)}
              </div>
              <fieldset class="fieldset">
                <legend class="visually-hidden">Address</legend>
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    required
                    ${register('address1')}
                    placeholder="First Address Line"
                    label="Address 1*"
                    autocomplete="address-line1"
                  ></ns-input-field>
                  ${renderFormErrorSpan('address1', this.__errors)}
                </div>
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    ${register('address2')}
                    placeholder="Second Address Line"
                    label="Address 2"
                    autocomplete="address-line2"
                  ></ns-input-field>
                </div>
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    required
                    ${register('city')}
                    placeholder="City"
                    label="City*"
                    autocomplete="address-level2"
                  ></ns-input-field>
                  ${renderFormErrorSpan('city', this.__errors)}
                </div>
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    required
                    ${register('postcode')}
                    placeholder="Postcode"
                    label="Postcode*"
                    autocomplete="postal-code"
                  ></ns-input-field>
                  ${renderFormErrorSpan('postcode', this.__errors)}
                </div>
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    required
                    ${register('country')}
                    placeholder="Country"
                    label="Country*"
                    autocomplete="country-name"
                  ></ns-input-field>
                  ${renderFormErrorSpan('country', this.__errors)}
                </div>
              </fieldset>
            </form>
          </div>
        `,
      )}
    `;
  }

  private __renderPaymentMethod() {
    const paymentMethod = this.__subscription?.paymentMethods?.find((pm) => pm.active);
    return html`
      <div class="job-option">
        <span>${paymentMethod?.customerName}</span>
        <span class="flex">**** **** **** ${paymentMethod?.last4}</span>
        <div
          class="brand-icon"
          style="background-image:url(/images/icons/${paymentMethod?.brand}-logo.svg)"
          role="img"
          aria-label="${paymentMethod?.brand ?? 'Card'} logo"
        ></div>
      </div>
    `;
  }

  private __renderPlanButton(plan: 'MONTHLY' | 'YEARLY') {
    const selected = this.__selectedPlan === plan;
    return html`
      <wa-button
        size="s"
        type="button"
        variant=${selected ? 'brand' : 'success'}
        ?disabled=${this.__subscription || this.__customerConfirmed}
        @click=${() => this.__selectPaymentPlan(plan)}
      >
        ${selected ? 'Selected' : 'Select'}
      </wa-button>
    `;
  }

  render() {
    return html`
      ${this.__announcer.render()}
      <ns-job-layout>
        ${this.__renderSubscriptionPlan()} ${this.__profile ? this.__renderCustomerForm() : nothing}
        ${this.__clientSecret && this.__succeeded !== true
          ? html`
              <div class="job-panel">
                <slot name="stripe-payment-form"></slot>
              </div>
            `
          : nothing}
        ${this.__succeeded === true
          ? html`
              <div class="job-panel payment-result">
                <h3>Payment Method</h3>
                ${this.__renderPaymentMethod()}
                <div class="layout vertical center">
                  <img alt="" src="/images/icons/success.svg" />
                  <div>Payment Successful!</div>
                </div>
              </div>
            `
          : nothing}
        ${when(
          this.__succeeded === false,
          () => html`
            <div class="job-panel">
              <p>TODO NEED TO DISCUSS WHAT DO WE COMMUNICATE BACK TO THE CUSTOMER IF IT IS NOT PAID</p>
            </div>
          `,
        )}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (this.__succeeded === true) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.outcome('complete')}>Complete</wa-button>
      `;
    }
    if (this.__clientSecret) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.__completePayment()}>Pay</wa-button>
      `;
    }
    if (this.__profile && !this.__customerDetails) {
      const { form } = this.__createFormController;
      return html`
        <wa-button variant="brand" type="button" ?disabled=${form.getState().submitting} @click=${() => form.submit()}>
          Confirm
        </wa-button>
      `;
    }
    return nothing;
  }
}
